import { Menu } from 'antd';
import React, { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import EcfmgHeader from '../../shared/ecfmg-header';
import ProLayout, { PageContainer } from '@ant-design/pro-layout';
import { PageLayoutProps } from '.';
import {
  UserOutlined,
  TeamOutlined,
  LockOutlined,
  LockFilled,
  IdcardOutlined,
  IdcardFilled,
  ContainerOutlined,
  ContainerFilled,
  ToolOutlined,
  ToolFilled,
  SettingOutlined,
  SettingFilled,
  DollarOutlined
} from '@ant-design/icons';
import { StaffRole, StaffSectionLayoutContainerStaffRoleFieldsFragment } from '../../../generated';
import AccessBlockedScreen from '../../shared/access-blocked-screen/access-blocked-screen';
import { ItemType } from 'antd/es/menu/interface';
import ImpendingMessage from '../../shared/maintenance-message/impending-message';
import { useMaintenanceMessage } from '../../shared/maintenance-message';
import MaintenanceMessage from '../../shared/maintenance-message/maintenance-message';
import { useTheme } from '../../shared/theme-context';
import DateInformationFooter from '../../shared/date-information-footer';

const iconMapping = {
  '1': { outlined: <ContainerOutlined />, filled: <ContainerFilled /> },
  '2': { outlined: <UserOutlined />, filled: <UserOutlined /> },
  '3': { outlined: <TeamOutlined />, filled: <TeamOutlined /> },
  '4': { outlined: <LockOutlined />, filled: <LockFilled /> },
  '5': { outlined: <SettingOutlined />, filled: <SettingFilled /> },
  '6': { outlined: <DollarOutlined />, filled: <DollarOutlined /> },
  '7': { outlined: <IdcardOutlined />, filled: <IdcardFilled /> },
  '8': { outlined: <ToolOutlined />, filled: <ToolFilled /> }
};

interface SectionLayoutProps {
  isAccessBlocked: boolean;
  pageLayouts: PageLayoutProps[];
  roleData: StaffSectionLayoutContainerStaffRoleFieldsFragment;
}

export const SectionLayout: React.FC<SectionLayoutProps> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const { isImpending, isMaintenance } = useMaintenanceMessage();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  useEffect(() => {
    const hasPermission = (layout: PageLayoutProps, roleData: StaffSectionLayoutContainerStaffRoleFieldsFragment) =>
      !layout.hasPermissions || (roleData && layout.hasPermissions(roleData as StaffRole));

    const layoutTitle = props.pageLayouts.find((layout) => hasPermission(layout, props.roleData))?.title ?? 'tools';

    if (location.pathname.includes(layoutTitle.toLowerCase())) {
      navigate(location.pathname);
    } else {
      navigate(`/staff/${layoutTitle.toLowerCase()}`);
    }
  }, []);

  const matchPartialRoute = (currentPath: string, routePath: string) => {
    return currentPath.startsWith(routePath);
  };

  const getMatchedPageIds = (pageLayouts: any[], location: { pathname: string }) => {
    const currentPath = location.pathname;

    return pageLayouts.filter((layout) => matchPartialRoute(currentPath, layout.path)).map((layout) => layout.id.toString());
  };

  const hasPermission = (layout: PageLayoutProps, roleData: StaffSectionLayoutContainerStaffRoleFieldsFragment) =>
    !layout.hasPermissions || (roleData && layout.hasPermissions(roleData as StaffRole));

  const matchedIds = getMatchedPageIds(props.pageLayouts, location);

  const menuItems = props.pageLayouts.map((layout) => {
    if (!hasPermission(layout, props.roleData)) {
      return null;
    }
    const isHovered = hoveredId === layout.id;
    const isActive = activeId === layout.id || matchedIds.includes(layout.id);

    const icon = iconMapping[layout.id as keyof typeof iconMapping];
    const iconStyle = { color: isHovered || isActive ? '#3f4373' : 'inherit', fontSize: '24px' };

    const renderIcon = icon
      ? isHovered || isActive
        ? React.cloneElement(icon.filled, { style: iconStyle })
        : React.cloneElement(icon.outlined, { style: iconStyle })
      : React.cloneElement(layout.icon, { style: iconStyle });

    return {
      id: layout.id as string,
      key: layout.id,
      label: (
        <Link to={layout.path}>
          {renderIcon}
          <div style={{ fontSize: '10px'}}>{layout.title}</div>
        </Link>
      ),
      style: {
        padding: '16px 24px',
        minHeight: '70px',
        minWidth: '50px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        lineHeight: '1.2',
        borderRadius: '0',
        backgroundColor: matchedIds.includes(layout.id) ? '#d3d6f8' : 'transparent',
        fontSize: '10px'
      },
      onClick: () => setActiveId(layout.id.toString()),
      onMouseEnter: () => setHoveredId(layout.id.toString()),
      onMouseLeave: () => setHoveredId(null)
    };
  });

  return (
    <ProLayout
      style={{ minHeight: '100vh', width: '100vw', top: isImpending || isMaintenance ? '40px' : '0px' }}
      logo={null}
      collapsedButtonRender={false}
      collapsed={true}
      title={false}
      defaultCollapsed={true}
      token={{
        sider: {
          colorMenuBackground: isDarkMode ? '#1f1f1f' : 'transparent',
          paddingInlineLayoutMenu: 0
        }
      }}
      menuContentRender={
        props.isAccessBlocked
          ? false
          : () => (
              <Menu
                items={menuItems as ItemType[]}
                defaultSelectedKeys={['1']}
                style={{
                  top: !(isImpending || isMaintenance) ? '50%' : undefined,
                  overflowY: 'auto',
                  height: '100%'
                }}
                selectedKeys={matchedIds}
              />
            )
      }
      footerRender={() => <DateInformationFooter />}
    >
      <EcfmgHeader />
      {isImpending && <ImpendingMessage />}
      {isMaintenance && <MaintenanceMessage />}
      <PageContainer style={{ width: '100vw', marginTop: isImpending || isMaintenance ? '95px' : undefined, height: '100%', marginLeft: '-20px' }}>
        {props.isAccessBlocked ? <AccessBlockedScreen /> : <Outlet />}
      </PageContainer>
    </ProLayout>
  );
};
